import { useCallback, useRef, useState, useEffect } from "react";

type UsePollingCallbackOptions = {
  interval?: number; // ms between polls (default: 2000)
  maxAttempts?: number; // max polls before giving up (default: 15 = 30s)
};

type UsePollingCallbackReturn = {
  isPolling: boolean;
  startPolling: (checkFn: () => Promise<boolean>) => void;
  stopPolling: () => void;
};

export function usePollingCallback(
  options: UsePollingCallbackOptions = {}
): UsePollingCallbackReturn {
  const { interval = 2000, maxAttempts = 15 } = options;

  const [isPolling, setIsPolling] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const attemptRef = useRef(0);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    attemptRef.current = 0;
    if (isMountedRef.current) {
      setIsPolling(false);
    }
  }, []);

  const startPolling = useCallback(
    (checkFn: () => Promise<boolean>) => {
      if (!isMountedRef.current) return;

      // Clear any existing interval to prevent overlapping polls
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      setIsPolling(true);
      attemptRef.current = 0;

      const poll = async () => {
        attemptRef.current += 1;

        try {
          const shouldStop = await checkFn();

          if (shouldStop || attemptRef.current >= maxAttempts) {
            stopPolling();
          }
        } catch {
          stopPolling();
        }
      };

      // Initial check after first interval
      intervalRef.current = setInterval(poll, interval);
    },
    [interval, maxAttempts, stopPolling]
  );

  return { isPolling, startPolling, stopPolling };
}
